import { getSession } from 'next-auth/react'
import { signOut } from 'next-auth/react'

export interface OpenidConfig {
  issuer: string
  token_endpoint: string
  scopes_supported: string[]
  response_types_supported: string[]
  response_modes_supported: string[]
  grant_types_supported: string[]
  token_endpoint_auth_methods_supported: string[]
  token_endpoint_auth_signing_alg_values_supported: string[]
  jwks_uri: string
  authorization_endpoint: string
  registration_endpoint: string
  end_session_endpoint: string
  check_session_iframe: string
  frontchannel_logout_supported: boolean
  frontchannel_logout_session_supported: boolean
  userinfo_endpoint: string
  subject_types_supported: string[]
  claims_parameter_supported: boolean
  id_token_signing_alg_values_supported: string[]
  id_token_encryption_alg_values_supported: string[]
  id_token_encryption_enc_values_supported: string[]
  userinfo_signing_alg_values_supported: string[]
  userinfo_encryption_alg_values_supported: string[]
  userinfo_encryption_enc_values_supported: string[]
  request_object_signing_alg_values_supported: string[]
  request_object_encryption_alg_values_supported: string[]
  request_object_encryption_enc_values_supported: string[]
  claim_types_supported: string[]
  claims_supported: string[]
}

export const getOidcConfig = async () => {
  const res = await fetch(
    `${process.env.NEXT_PUBLIC_STACKUP_HOST}/.well-known/openid-configuration`
  )
  return (await res.json()) as OpenidConfig
}

export const fetchEndSessionEndpoint = async () => {
  try {
    return (await getOidcConfig()).end_session_endpoint
  } catch (error) {
    console.error('Error fetching OIDC configuration:', error)
    return null
  }
}

// The IdP sets a domain-wide `sso` cookie on the parent domain (e.g.
// `.stackup.example`) that is shared with this app. The middleware treats a
// present `sso` cookie as "an SSO session may exist" and bounces guests
// straight back into a silent `signIn('stackup')` — so unless this cookie is
// cleared, logging out immediately re-authenticates the user. The IdP's
// end-session endpoint clears it too, but we also clear it here so logout is
// deterministic even if that round-trip is interrupted or rejected.
const clearSsoCookie = () => {
  if (typeof window === 'undefined') return

  const parts = window.location.hostname.split('.')
  const parentDomain =
    parts.length > 2 ? `.${parts.slice(-2).join('.')}` : window.location.hostname

  document.cookie = `sso=; Max-Age=0; path=/; domain=${parentDomain}`
  document.cookie = `sso=; Max-Age=0; path=/`
}

/**
 * Clear the reviewer session cookie (`bb-reviewer-auth`). It is httpOnly, so it
 * can only be deleted server-side — best-effort POST to the logout route.
 * Harmless no-op for non-reviewer users.
 */
const clearReviewerCookie = async () => {
  if (typeof window === 'undefined') return
  try {
    await fetch('/api/reviewer/logout', { method: 'POST' })
  } catch {
    // best effort — the IdP logout + sso-cookie clear still apply
  }
}

// Cross-tab logout: a logout in one tab broadcasts here so every other open
// tab (web / admin / manager / reviewer — including reviewer-only tabs without
// a live NextAuth session that the OIDC check-session iframe can't reach) tears
// its session down immediately rather than waiting on the ~5s iframe poll.
const AUTH_BROADCAST_CHANNEL = 'stackup-auth'

// Per-tab id so a tab ignores its OWN broadcast — BroadcastChannel delivers to
// other channel instances in the same tab too, and the initiating tab must NOT
// short-circuit its IdP end-session redirect by reacting to its own message.
const TAB_ID =
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.floor(Math.random() * 1e9)}`

interface LogoutBroadcast {
  kind: 'logout'
  sender: string
}

const broadcastLogout = () => {
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
    return
  }
  try {
    const ch = new BroadcastChannel(AUTH_BROADCAST_CHANNEL)
    ch.postMessage({ kind: 'logout', sender: TAB_ID } satisfies LogoutBroadcast)
    ch.close()
  } catch {
    // BroadcastChannel unsupported — the OIDC iframe still covers same-IdP tabs
  }
}

export const subscribeToLogout = (handler: () => void): (() => void) => {
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
    return () => {}
  }
  const ch = new BroadcastChannel(AUTH_BROADCAST_CHANNEL)
  const listener = (e: MessageEvent) => {
    const data = e.data as LogoutBroadcast | null
    if (data?.kind === 'logout' && data.sender !== TAB_ID) handler()
  }
  ch.addEventListener('message', listener)
  return () => {
    ch.removeEventListener('message', listener)
    ch.close()
  }
}

/**
 * Tear down the session in THIS tab without a round-trip to the IdP end-session
 * endpoint (the IdP session is already gone — this runs when logout was
 * initiated elsewhere: another tab, a frontchannel logout, or a token-refresh
 * failure). Clears the sso cookie + reviewer cookie + NextAuth session, then
 * lands on a guest page so no stale authenticated view is left behind.
 *
 * @param options.broadcast notify other tabs (default false — set true only for
 *   the tab that first detected the logout, to avoid a rebroadcast loop).
 */
export const localSignOut = async (options?: {
  broadcast?: boolean
  redirectTo?: string
}): Promise<void> => {
  const { broadcast = false, redirectTo = '/' } = options ?? {}
  clearSsoCookie()
  await clearReviewerCookie()
  if (broadcast) broadcastLogout()
  await signOut({ redirect: false })
  if (typeof window !== 'undefined') window.location.href = redirectTo
}

export const oidcSignOut = async (end_session_endpoint?: string) => {
  let url = '/'

  if (end_session_endpoint) {
    const session = await getSession()

    const logout_url = new URL(end_session_endpoint)
    logout_url.searchParams.set(
      'post_logout_redirect_uri',
      window.location.origin
    )

    if (session?.idToken) {
      logout_url.searchParams.set('id_token_hint', session?.idToken)
    }

    url = logout_url.toString()
  }

  clearSsoCookie()
  // A full logout also drops the separate reviewer session, so leaving any
  // portal logs the user out everywhere.
  await clearReviewerCookie()
  // Tell other tabs to tear down immediately (don't wait on the iframe poll).
  broadcastLogout()

  // Clear the local NextAuth session deterministically (without relying on the
  // `redirect` callback to forward an external callbackUrl), then navigate to
  // the IdP end-session endpoint to terminate the shared SSO session.
  await signOut({ redirect: false })
  window.location.href = url
}
